import { Badge, Card, Grid, Group, Stack, Text, Switch, ThemeIcon } from '@mantine/core';
import { SiStripe, SiPaypal } from 'react-icons/si';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchEnabledBillingProviders, enableBillingProviderForCurrentCustomer, disableBillingProviderForCurrentCustomer } from '../services/BillingProvidersService';
import { fetchProviderMappings, providerMappingMatchesProvider, providerMappingIsActive } from '../../provider-mappings/services/ProviderMappingsService';
import type { ProviderMapping } from '../../provider-mappings/types';
import type { BillingProvider } from '../types';
import { useUserAccount } from '../../../../hooks/useUserAccount';
import { SimplePageLayout, SimplePageContentWrapper, PlatformLoader } from '@flexinet/ui-components';
import { usePaymentProfiles } from '../../payment-profiles/hooks/usePaymentProfiles';
import { IndexPageHeader } from '../components/IndexPageHeader';
import { IndexPageNavigation } from '../navigation/IndexPageNavigation';

const providerIconMap: Record<string, typeof SiStripe> = {
    stripe: SiStripe,
    paypal: SiPaypal,
};

const providerColorMap: Record<string, string> = {
    stripe: 'violet',
    paypal: 'blue',
};

const providerDescriptions: Record<string, string> = {
    stripe: 'Accept credit cards, debit cards, and other payment methods with Stripe.',
    paypal: 'Enable PayPal payments for your customers worldwide.',
};

export const BillingProviderSelectionPage = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { userAccount } = useUserAccount();
    const { paymentProfiles, loading: profilesLoading } = usePaymentProfiles();

    const { data: providers, isLoading: providersLoading } = useQuery({
        queryKey: ['billing-providers'],
        queryFn: fetchEnabledBillingProviders
    });

    const { data: providerMappings, isLoading: mappingsLoading } = useQuery({
        queryKey: ['provider-mappings', userAccount?.customerId],
        queryFn: () => fetchProviderMappings({ localEntityId: userAccount?.customerId, entityType: 'Customer' }),
        enabled: !!userAccount?.customerId
    });

    const updateMappingMutation = useMutation({
        mutationFn: async ({ provider }: { provider: BillingProvider }) => {
            // FNP-741: decide the toggle direction from the ACTIVE state, not mapping presence.
            // A retained-but-inactive mapping must toggle to ENABLE (which reactivates it and
            // reuses the existing cus_), not disable.
            const activeMapping = providerMappings?.find((m: ProviderMapping) =>
                providerMappingMatchesProvider(m, provider) && providerMappingIsActive(m)
            );

            if (activeMapping) {
                return await disableBillingProviderForCurrentCustomer(provider.id);
            } else {
                return await enableBillingProviderForCurrentCustomer(provider.id);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['provider-mappings'] });
        }
    });

    const handleToggleProvider = (provider: BillingProvider) => {
        updateMappingMutation.mutate({ provider });
    };

    if (providersLoading || profilesLoading || mappingsLoading) {
        return <PlatformLoader message="Loading billing providers..." />;
    }

    return (
        <SimplePageLayout
            pageHeader={<IndexPageHeader />}
            pageNavigation={<IndexPageNavigation />}
        >
            <SimplePageContentWrapper>
                {providers && providers.length > 0 ? (
                    <Grid>
                        {providers.map((provider: BillingProvider) => {
                            const providerProfiles = paymentProfiles.filter(
                                (profile) => profile.billingProviderId === provider.id || profile.billingProvider?.id === provider.id
                            );
                            // FNP-741: enabled == a mapping for this provider exists AND is active.
                            // A retained inactive mapping (post-disable) is present but reads Disabled.
                            const isEnabled = !!providerMappings?.find((m: ProviderMapping) =>
                                providerMappingMatchesProvider(m, provider) && providerMappingIsActive(m)
                            );
                            const ProviderIcon = providerIconMap[provider.key];

                            return (
                                <Grid.Col key={provider.id} span={{ base: 12, sm: 6, lg: 4 }}>
                                    <Card
                                        withBorder
                                        p="lg"
                                        h="100%"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => navigate(`/billing/providers/${provider.id}`)}
                                    >
                                        <Stack gap="md" justify="space-between" h="100%">
                                            <Stack gap="md">
                                                <Group justify="space-between" align="flex-start">
                                                    {ProviderIcon && (
                                                        <ThemeIcon variant="light" size="xl" radius="md" color={providerColorMap[provider.key] || 'gray'}>
                                                            <ProviderIcon size={24} />
                                                        </ThemeIcon>
                                                    )}
                                                    <div onClick={(e) => e.stopPropagation()}>
                                                        <Switch
                                                            checked={isEnabled}
                                                            onChange={() => handleToggleProvider(provider)}
                                                        />
                                                    </div>
                                                </Group>

                                                <Stack gap="xs">
                                                    <Group gap="sm">
                                                        <Text size="lg" fw={600}>{provider.name}</Text>
                                                        <Badge color={isEnabled ? 'green' : 'gray'} size="sm">
                                                            {isEnabled ? 'Enabled' : 'Disabled'}
                                                        </Badge>
                                                    </Group>
                                                    <Text size="sm" c="dimmed">
                                                        {provider.description || providerDescriptions[provider.key] || 'Payment provider'}
                                                    </Text>
                                                </Stack>
                                            </Stack>

                                            <Text size="xs" c="dimmed">
                                                {providerProfiles.length > 0
                                                    ? `${providerProfiles.length} payment method${providerProfiles.length !== 1 ? 's' : ''} configured`
                                                    : 'No payment methods configured'}
                                            </Text>
                                        </Stack>
                                    </Card>
                                </Grid.Col>
                            );
                        })}
                    </Grid>
                ) : (
                    <Text c="dimmed" ta="center" py="xl">
                        No billing providers available.
                    </Text>
                )}
            </SimplePageContentWrapper>
        </SimplePageLayout>
    );
};
